// Angular
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
// RxJS
import { mergeMap, tap, withLatestFrom, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
// Auth actions
import {
  AuthActionTypes,
  Login,
  Logout,
  UserLoaded,
  UserRequested,
  RequestPassword,
  AuthPageToggleLoading,
  AuthActionToggleLoading,
  RequestQvTokens,
  SetQvTokens,
  SetTwixorToken,
  Reload,
} from '../_actions/auth.actions';
import { AuthService } from '../_services/index';
import { AppState } from '../../reducers';
import { environment } from '../../../../environments/environment';
import { isUserLoaded } from '../_selectors/auth.selectors';
import { AuthNoticeService } from '../auth-notice/auth-notice.service';
import { TranslateService } from '@ngx-translate/core';
import { AllRolesRequested } from '../_actions/role.actions';
import { AllPermissionsRequested } from '../_actions/permission.actions';
import { GlobalSettingsService } from '../../services/global-settings.service';
import { TwixorService } from '../../services/twixor.service';
import { ThemeService } from '../../../views/theme/services/theme.service';

// new edit on function login
@Injectable()
export class AuthEffects {
  showPageLoadingDistpatcher = new AuthPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new AuthPageToggleLoading({ isLoading: false });

  showActionLoadingDistpatcher = new AuthActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new AuthActionToggleLoading({ isLoading: false });

  @Effect({ dispatch: false })
  login$ = this.actions$.pipe(
    ofType<Login>(AuthActionTypes.Login),
    tap(() => {
      this.store.dispatch(new UserRequested());
    }),
  );

  @Effect({ dispatch: false })
  reload$ = this.actions$.pipe(
    ofType<Reload>(AuthActionTypes.Reload),
    tap(() => {
      this.store.dispatch(new UserRequested());
    }),
  );

  @Effect({ dispatch: false })
  logout$ = this.actions$.pipe(
    ofType<Logout>(AuthActionTypes.Logout),
    switchMap((action: Logout) => {
      const performLocalLogout = () => {
        localStorage.clear();
        this.globalSettings.setFilteredView(false);
        this.authService.setRedirectUrl(action.saveURL ? this.router.url : '');
        this.authService.setUser(null);
        if (!action.payload.fromBroadCast) {
          this.authService.broadcastLogout();
        }
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: this.returnUrl },
        });

        this.store.dispatch(new AllRolesRequested());
        this.store.dispatch(new AllPermissionsRequested());
      };

      if (!action.payload?.onSignout) {
        performLocalLogout();
        return of(null);
      }
      return this.authService.signOut().pipe(
        tap({
          next: () => {
            performLocalLogout();
          },
          error: (err) => {
            console.error(err);
          },
        }),
      );
    }),
  );

  @Effect({ dispatch: false })
  loadUser$ = this.actions$.pipe(
    ofType<UserRequested>(AuthActionTypes.UserRequested),
    withLatestFrom(this.store.pipe(select(isUserLoaded))),
    mergeMap(() => this.auth.getUserByToken()),
    tap((_user) => {
      if (_user) {
        localStorage.setItem(environment.qvUserId, _user[`qrveyUserId`]);
        this.themeService.minimized = !_user.userPreferences?.menu_expanded;
        this.store.dispatch(new UserLoaded({ user: _user }));
        if (_user.agent) {
          this.twixorService.loginToTwixor().subscribe((res) => {
            if (res && res.twixor_token) {
              this.store.dispatch(new SetTwixorToken({ twixor_token: res.twixor_token }));
              localStorage.setItem('twixor_token', res.twixor_token);
            }
          });
        }
      } else {
        this.store.dispatch(new Logout());
      }
    }),
  );

  @Effect()
  requestpassword$ = this.actions$.pipe(
    ofType<RequestPassword>(AuthActionTypes.RequestPassword),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.auth.requestPassword(payload.email).pipe(
        map((res) => {
          if (res[`code`] === 404) {
            this.authNoticeService.setNotice(this.translate.instant('AUTH.NOT_FOUND'), 'danger');
          } else {
            this.authNoticeService.setNotice('please check your email', 'success');
            return this.hideActionLoadingDistpatcher;
            // this.router.navigate(['/auth/login']);
          }

          return this.hideActionLoadingDistpatcher;
        }),
      );
    }),
  );

  @Effect()
  requestQvTokens$ = this.actions$.pipe(
    ofType<RequestQvTokens>(AuthActionTypes.RequestQvTokens),
    mergeMap(({ payload }) => {
      return this.auth.getQvToken(payload).pipe(
        map((res) => {
          if (res.data.token) {
            localStorage.setItem(environment.qvToken, res.data.token);
            return new SetQvTokens({ qv_token: res.data.token });
          } else {
            return of({ type: 'NO_ACTION' });
          }
        }),
      );
    }),
  );

  private returnUrl: string;

  constructor(
    private actions$: Actions,
    private router: Router,
    private authService: AuthService,
    private auth: AuthService,
    private store: Store<AppState>,
    private authNoticeService: AuthNoticeService,
    private translate: TranslateService,
    private globalSettings: GlobalSettingsService,
    private themeService: ThemeService,
    private twixorService: TwixorService,
  ) {
    this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationEnd &&
        event.url.includes('contacts') &&
        event.url.includes('phone')
      ) {
        this.returnUrl = event.url;
      }
    });
  }
}
